import { Box, Grid, Typography } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select'
import React, { ReactNode } from 'react'
import { Preloader } from '../../../common'
import InputLabel from '@mui/material/InputLabel'

export type ISelect = {
  label: string
  value: string
}

type SelectInputProps = {
  isDisabled?: boolean
  value: ISelect
  label?: string
  labelColor?: string
  isLabelBold?: boolean
  labelLegend?: string | ReactNode
  options: ISelect[]
  onChange: (option: ISelect, index?: number) => void
  id?: string
  instanceId?: string
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
  onMenuScrollToBottom?: () => void
  isLoading?: boolean
  isLoadingMore?: boolean
  noResultsText?: string
  showPlaceholder?: boolean
  shouldShrinkLegend?: boolean
  'data-testid'?: string
} & Omit<SelectProps, 'onChange' | 'value'>

export const SelectInput: React.FC<SelectInputProps> = ({
  isDisabled,
  value,
  label,
  labelColor,
  isLabelBold = true,
  options,
  onChange,
  id,
  instanceId,
  onKeyDown,
  onMenuScrollToBottom,
  isLoading = false,
  isLoadingMore = false,
  noResultsText = 'No options',
  showPlaceholder = false,
  shouldShrinkLegend = false,
  labelLegend,
  'data-testid': dataTestId,
  ...selectProps
}) => {
  // data-testid has to land on the display element (the one with role='combobox'), not the outer wrapper,
  // so tests can both find and open the dropdown by test id
  const selectDisplayProps: React.HTMLAttributes<HTMLDivElement> & { 'data-testid'?: string } = {
    ...selectProps.SelectDisplayProps,
    'data-testid': dataTestId,
  }

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string
    const selectedOption = options.find((option) => option.value === selectedValue)
    if (selectedOption) {
      onChange(selectedOption)
    }
  }

  return (
    <Grid
      container
      spacing={2}
    >
      {label && (
        <Grid
          item
          xs={12}
        >
          <Typography
            fontWeight={isLabelBold ? 'bold' : 'regular'}
            color={labelColor}
            style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}
          >
            {label}
          </Typography>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        className='select-input'
      >
        <FormControl
          fullWidth
          variant='outlined'
          disabled={isDisabled}
        >
          <InputLabel
            id={`${id}-label`}
            shrink={shouldShrinkLegend}
          >
            {labelLegend}
          </InputLabel>
          <Select
            labelId={`${id}-label`}
            id={id}
            size='small'
            value={value.value}
            displayEmpty
            label={labelLegend}
            onChange={handleChange}
            inputProps={{ 'data-instance-id': instanceId }}
            onKeyDown={onKeyDown}
            sx={{ backgroundColor: 'white', height: '48px' }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
                onScroll: (event: React.UIEvent<HTMLElement>) => {
                  const bottom = event.currentTarget.scrollHeight === event.currentTarget.scrollTop + event.currentTarget.clientHeight
                  if (bottom && onMenuScrollToBottom) {
                    onMenuScrollToBottom()
                  }
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
            renderValue={(selected) =>
              showPlaceholder && (!selected || selected === '') ? (
                <Typography
                  noWrap
                  sx={{ color: '#9e9e9e' }}
                >
                  Please select
                </Typography>
              ) : (
                <Typography
                  noWrap
                  sx={{ textOverflow: 'ellipsis' }}
                >
                  {value.label}
                </Typography>
              )
            }
            {...selectProps}
            SelectDisplayProps={selectDisplayProps}
          >
            {options.length > 0
              ? [
                  ...options.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  )),
                  isLoadingMore && (
                    <MenuItem
                      key='loading-more'
                      disabled
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <Preloader size='xs' />
                      </Box>
                    </MenuItem>
                  ),
                ].filter(Boolean)
              : isLoading
                ? [
                    <MenuItem
                      key='loading'
                      disabled
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <Preloader size='xs' />
                      </Box>
                    </MenuItem>,
                  ]
                : [
                    <MenuItem
                      key='no-results'
                      disabled
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <Typography>{noResultsText}</Typography>
                      </Box>
                    </MenuItem>,
                  ]}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  )
}
